import React, { useState, useEffect, useRef, useContext } from 'react';
import { TextField as TdsTextField, FormItem } from '@tesla/react-tds/global';
import Option from './Option';
import OptionGroup, { OptionGroupContext } from './OptionGroup';
import useDebounce from '../../hooks/useDebounce';
import useFormFieldApi from '../../hooks/useFormFieldApi';
import useFormFieldState from '../../hooks/useFormFieldState';
import useOutsideAlerter from '../../hooks/useOutsideAlerter';

const DropdownInput = ({
  openDropdown,
  closeDropdown,
  open,
  nItems,
  sItems,
  sItmesAndCount,
  label,
  id,
  error,
  errorLabel,
  disabled,
  name,
}) => {
  const { value: selected } = useFormFieldState(name);

  const handleClick = () => {
    if (open) {
      closeDropdown();
    } else {
      openDropdown();
    }
  };

  return (
    <FormItem error={error} errorLabel={errorLabel} id={id} label={label}>
      <div className="tds-select">
        <input
          className="tds-text-input"
          onClick={handleClick}
          role="listbox"
          autoComplete="off"
          disabled={disabled}
          placeholder={`Select ${label}`}
          aria-expanded={!!open}
          value={selected && selected.length > 0 ? `${nItems} ${sItems} ${sItmesAndCount}` : ''}
          readOnly
        />
        <i className="tds-icon tds-icon-arrow-down tds-select-arrow" />
      </div>
    </FormItem>
  );
};

const Options = ({
  showSelectedOptions,
  options,
  filteredOptions,
  name,
  selectedOptionsClassName,
  unSelectedOptionsClassName,
  optionGroupClassname,
}) => {
  const { optionGroupContext } = useContext(OptionGroupContext);
  const { id } = optionGroupContext;
  const { value: selected } = useFormFieldState(name);

  if (showSelectedOptions) {
    const unSelectedOptions = selected
      ? filteredOptions.filter((option) => !selected.includes(option.value))
      : filteredOptions;
    const selectedOptions = selected
      ? options.filter((option) => selected.includes(option.value))
      : [];

    return (
      <div
        style={{
          width: '100%',
        }}
        className={optionGroupClassname || ''}
      >
        {selected && selected.length > 0 ? (
          <div
            style={{
              width: '100%',
              marginBottom: '1rem',
            }}
            className={selectedOptionsClassName || ''}
          >
            {selectedOptions.map((option) => (
              <Option key={`${id}-${option.value}`} value={option.value} label={option.label} />
            ))}
          </div>
        ) : null}

        <div
          style={{
            width: '100%',
          }}
          className={unSelectedOptionsClassName || ''}
        >
          {unSelectedOptions.map((option) => (
            <Option key={`${id}-${option.value}`} value={option.value} label={option.label} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      {filteredOptions.map((option) => (
        <Option key={`${id}-${option.value}`} value={option.value} label={option.label} />
      ))}
    </React.Fragment>
  );
};

const SelectSearch = ({
  label,
  includes,
  showSelectedCount,
  showLabelsThenCount,
  showSelectedButton,
  showSelectedLabels,
  optionsClassname,
  containerClassname,
  optionsOpenClassname,
  selectedOptionsClassName,
  unSelectedOptionsClassName,
  optionGroupClassname,
  resetOnSearch,
  options,
  maxHight,
  maxHeight,
  noScroll,
  showSelectedOptions,
  placeholder,
  noSearch,
  dropDown,
  id,
  ...rest
}) => {
  const { reset } = useFormFieldApi(rest.name);
  const { value: selected, error, $showError } = useFormFieldState(rest.name);
  const { disabled } = rest;

  // The value of search box
  const [value, setValue] = useState();

  // The filtered options
  const [filteredOptions, setFilteredOptions] = useState(options);

  // The devounced value
  const debouncedValue = useDebounce(value, 250);

  // View selected
  const [viewSelected, setViewSelected] = useState(false);

  // For dropdown
  const [open, setOpen] = useState(false);

  const closeDropdown = () => {
    setOpen(false);
  };

  const openDropdown = () => {
    setOpen(true);
  };

  const ref = useRef(null);
  useOutsideAlerter(ref, closeDropdown);

  // Effect for search
  useEffect(
    () => {
      if (viewSelected) {
        setFilteredOptions(options.filter((option) => selected && selected.includes(option.value)));
      } else if (debouncedValue) {
        setViewSelected(false);
        if (resetOnSearch) {
          reset();
        }
        if (includes) {
          setFilteredOptions(
            options.filter((option) => {
              const searchCritiria = debouncedValue
                .split(',')
                .map((r) => r.trim())
                .filter((r) => r !== '');
              return searchCritiria.some((r) => {
                const aa = r
                  .split('+')
                  .map((r) => r.trim())
                  .filter((r) => r !== '');
                return aa.every((s) => option.label.includes(s));
              });
            })
          );
        } else {
          setFilteredOptions(
            options.filter((option) => {
              const searchCritiria = debouncedValue
                .split(',')
                .map((r) => r.trim())
                .filter((r) => r !== '');
              return searchCritiria.some((r) => {
                const aa = r
                  .split('+')
                  .map((r) => r.trim())
                  .filter((r) => r !== '');
                return aa.every((s) => option.label.startsWith(s));
              });
            })
          );
        }
      } else {
        setFilteredOptions(options);
      }
    },
    [debouncedValue, options.length, viewSelected] // Only call effect if debounced search term changes
  );

  // Set the value on input change
  const onChange = (e) => {
    setValue(e.target.value);
  };

  const nItems = showSelectedCount
    ? `${selected ? selected.length : 0}/${options.length} items selected`
    : '';

  const selectedOptions = options.filter((option) => selected && selected.includes(option.value));
  const selectedLabels = selectedOptions.map((opt) => opt.label);

  const sItems =
    showSelectedLabels && selected && selected.length > 0 ? `${selectedLabels.join(' - ')}` : '';

  const sItmesAndCount =
    showLabelsThenCount && selected && selected.length > 0
      ? `${selectedLabels.slice(0, +showLabelsThenCount).join(' - ')}${
          selectedLabels.length > showLabelsThenCount
            ? ` + ${selectedLabels.length - showLabelsThenCount}`
            : ''
        }`
      : '';

  const style = {
    overflowY: noScroll ? 'visible' : 'scroll',
    marginBottom: '1rem',
    width: '100%',
  };

  if (!noScroll) {
    style.maxHeight = maxHight || maxHeight || '300px';
  }

  // Return input and option group
  return (
    <div ref={ref} className={containerClassname}>
      {!noSearch && !dropDown ? (
        <TdsTextField
          onChange={onChange}
          label={`${label} ${nItems} ${sItems} ${sItmesAndCount}`}
          disabled={disabled}
          onFocus={openDropdown}
          placeholder={placeholder || 'Search'}
          error={$showError}
        />
      ) : null}
      {dropDown ? (
        <DropdownInput
          label={label}
          disabled={disabled}
          openDropdown={openDropdown}
          closeDropdown={closeDropdown}
          open={open}
          error={$showError}
          errorLabel={error}
          name={rest.name}
          nItems={nItems}
          sItems={sItems}
          sItmesAndCount={sItmesAndCount}
        />
      ) : null}
      <FormItem
        error={!dropDown && $showError}
        errorLabel={!dropDown && error}
        label={noSearch && !dropDown ? `${label} ${nItems} ${sItems} ${sItmesAndCount}` : ''}
      >
        <div
          style={style}
          className={`${optionsClassname || ''} ${open ? optionsOpenClassname : ''}`}
        >
          {dropDown && !noSearch ? (
            <TdsTextField
              style={{ marginBottom: '1rem' }}
              onChange={onChange}
              disabled={disabled}
              placeholder={placeholder || 'Search'}
            />
          ) : null}
          <OptionGroup {...rest} noFormItem>
            <Options
              optionGroupClassname={optionGroupClassname}
              unSelectedOptionsClassName={unSelectedOptionsClassName}
              selectedOptionsClassName={selectedOptionsClassName}
              showSelectedOptions={showSelectedOptions}
              options={options}
              filteredOptions={filteredOptions}
              name={rest.name}
            />
          </OptionGroup>
        </div>
      </FormItem>
    </div>
  );
};

export default SelectSearch;
